import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { WarehouseElementLocation } from "components/WarehouseElements/WarehouseElementsData";
import { NotificationType } from "constants/NotificationConstants";
import { NotificationResource } from "models/Resources/NotificationResource";
import { RootState } from "store/store";
import { isNotificationExpired } from "utils/isNotificationExpired";

interface GridEventState {
  openedDetailId: string | null;
  searchedItemId: string | null;
  searchedItemRefreshToken: number;
  notificationsList: NotificationResource[];
  newImportantNotification: NotificationResource | undefined;
  selectedShortcut: string | null;
  unfoundItemId: string | undefined;
  urlParamGridPosition: WarehouseElementLocation;
}

const initialState = {
  openedDetailId: null,
  searchedItemId: null,
  searchedItemRefreshToken: 0,
  notificationsList: [],
  newImportantNotification: undefined,
  selectedShortcut: null,
  unfoundItemId: undefined,
  urlParamGridPosition: { start: { x: 0, y: 0 } },
} satisfies GridEventState as GridEventState;

export const gridEventSlice = createSlice({
  name: "gridEventSlice",
  initialState,
  reducers: {
    setOpenedDetailId(state, action: PayloadAction<string | null>) {
      state.openedDetailId = action.payload;
    },
    setSearchedItemId(state, action: PayloadAction<string | null>) {
      state.searchedItemId = action.payload;
      state.searchedItemRefreshToken += 1;
    },
    updateNotification(state, action: PayloadAction<NotificationResource>) {
      const incomingNotification = action.payload;
      if (
        incomingNotification.expiryDate
          ? isNotificationExpired(action.payload, Date.now())
          : false
      ) {
        const toremoveNotificationIndex = state.notificationsList.findIndex(
          notification =>
            notification.notificationId == incomingNotification.notificationId
        );
        if (toremoveNotificationIndex >= 0) {
          state.notificationsList.splice(toremoveNotificationIndex, 1);
        }
        return;
      }
      const changedNotificationIndex = state.notificationsList.findIndex(
        notification =>
          notification.notificationId == incomingNotification.notificationId
      );
      if (changedNotificationIndex >= 0) {
        state.notificationsList[changedNotificationIndex] = action.payload;
      } else {
        if (
          incomingNotification.type == NotificationType.IMPORTANT &&
          !incomingNotification.archivedBy
        ) {
          state.newImportantNotification = action.payload;
        }
        state.notificationsList.push(action.payload);
        state.notificationsList = sortAndFilterNotifications(
          state.notificationsList
        );
      }
    },
    setNotificationList(state, action: PayloadAction<NotificationResource[]>) {
      state.notificationsList = action.payload;
    },
    addUnfoundItemId(state, action: PayloadAction<string>) {
      state.unfoundItemId = action.payload;
    },
    resetGridEvent(state) {
      state.searchedItemId = null;
      state.openedDetailId = null;
      state.searchedItemRefreshToken = 0;
      state.notificationsList = [];
      state.newImportantNotification = undefined;
      state.unfoundItemId = undefined;
    },
    setURLParamDefaultPosition(state, action: PayloadAction<string>) {
      const position = action.payload.split(",");
      if (+position[0] != 0 && +position[1] != 0) {
        state.urlParamGridPosition.start.y = +position[0];
        state.urlParamGridPosition.start.x = +position[1];
      }
    },
  },
});

export const {
  setOpenedDetailId,
  setSearchedItemId,
  updateNotification,
  setNotificationList,
  resetGridEvent,
  addUnfoundItemId,
  setURLParamDefaultPosition,
} = gridEventSlice.actions;
export const getOpenedDetailId = (state: RootState): string | null =>
  state.gridEventSlice.openedDetailId;
export const getSearchedItemId = (state: RootState): string | null =>
  state.gridEventSlice.searchedItemId;
export const getUnfoundItemId = (state: RootState): string | undefined =>
  state.gridEventSlice.unfoundItemId;
export const getNewImportantNotification = (
  state: RootState
): NotificationResource | undefined =>
  state.gridEventSlice.newImportantNotification;

export default gridEventSlice.reducer;

export function sortAndFilterNotifications(
  notificationsResources: NotificationResource[]
): NotificationResource[] {
  const dateNow = Date.now();
  const notExpiredNotifications = notificationsResources.filter(
    notification => !isNotificationExpired(notification, dateNow)
  );
  const sortedNotifications = notExpiredNotifications.sort((a, b) =>
    b.sentDate.toISOString().localeCompare(a.sentDate.toISOString())
  );
  return sortedNotifications;
}
